import { useClientSettingStore, useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { DataAccessUtils } from '../../model/DataAccessUtils';
import { DealScore } from '../DealDetails/DealScore/DealScore';
import { DealScoreCard } from '../DealDetails/DealScore/DealScoreCard';
import {
    formatDollar,
    formatPercent,
    formatPercentNoMultiply,
    noFormat,
    noFormatRaw
} from '../Financial/PanelCompanyYearly/PanelElements/Formatters';
import { RowLabel } from '../Financial/PanelCompanyYearly/PanelElements/RowLable/RowLable';
import { RowValue } from '../Financial/PanelCompanyYearly/PanelElements/RowValue/RowValue';
import {
    PanelColumn,
    PanelRow,
    PanelTable
} from '../Panel/PanelTable/PanelTable';

import { CompanyCompareUtils } from '@assets/model/CompanyCompareUtils';
import { DataAccessAggregateUtils } from '@assets/model/DataAccessAggregateUtils';
import {
    ExtendedFieldsSchema,
    ExtendedSchemaField
} from '@assets/model/FieldsSchema';
import {
    getRules,
    RuleType,
    ScoreFieldsGroup,
    ScoreUtils,
    useScoreStore
} from '@assets/model/ScoreUtils';
import { useNavUtils } from '@assets/utils/NavUtils';
import classNames from 'classnames';
import { assert } from 'console';
import { ImageUI } from '../Image/Image';
import { PanelTitle } from '../Panel/PanelTitle/PanelTitle';
import { ScrollPanel } from '../Panel/ScrollPanel/ScrollPanel';
import { SectionIcon } from '../SectionIcons/SectionIcon';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DealCompare.module.less';
import industryIcon from './industry_icon.svg';
import { formatPercentStr } from '@assets/utils/Formatting';
import { isString } from 'lodash';
import { Fragment } from 'react';
import { Icon } from '../Icons/Icons';
import { Modal } from 'react-bootstrap';
import { DealModalSearchContent } from '../DealSearch/DealModalSearchContent';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const DealCompare = observer(() => {
    const store = useStore();
    const clientStore = useClientSettingStore();
    const navUtils = useNavUtils();
    const [showComparableSearch, setShowComparableSearch] = useState(false);
    const [comparableClickedPosition, setComparableClickedPosition] = useState<number | null>(null);
    const INDUSTRY_AVG_ID = 'INDUSTRY_AVERAGE';
    const SECTIONS_SHOW_NOT_SCORED = ['overview'];

    if (store.selectedReportId == '' || store.query == null) return null;
    const q = store.query;

    const getQ = (reportId: string) => {
        if (reportId === 'ADD_BUTTON') {
            return null;
        }
        if (reportId === INDUSTRY_AVG_ID) {
            return aggAccess;
        }
        return store.createDataAccess(reportId);
    };

    const handleRemoveComparable = (position: number) => {
        const updatedComparables = [...store.userComparables];
        updatedComparables[position] = null;
        while (updatedComparables.length < 2) {
            updatedComparables.push(null);
        }
        store.saveUserComparables(store.selectedReportId, updatedComparables);
    };

    const handleComparableSelect = (dealId: string) => {
        if (comparableClickedPosition === null) return;
        
        const updatedComparables = [...store.userComparables];
        updatedComparables[comparableClickedPosition] = dealId;
        while (updatedComparables.length < 2) {
            updatedComparables.push(null);
        }
        store.saveUserComparables(store.selectedReportId, updatedComparables);
        setShowComparableSearch(false);
        setComparableClickedPosition(null);
    };

    // Load default comparables if none exist
    useEffect(() => {
        if (store.selectedReportId && !store.allUserComparables[store.selectedReportId]) {
            const defaultComparables = CompanyCompareUtils.findCompaniesToCompare(store.selectedReportId, 2);
            store.initializeUserComparables(store.selectedReportId, defaultComparables);
        }
    }, [store.selectedReportId]);

    const companies = [
        store.selectedReportId,
        ...store.userComparables.filter(dealId => dealId !== null && dealId !== undefined)
    ];

    // Create an array of columns with fixed positions
    const columnsData = [
        store.selectedReportId,
        ...store.userComparables,
        INDUSTRY_AVG_ID
    ];

    const dataAccess = { string: DataAccessUtils };
    const mainDeal = store.createDataAccess(store.selectedReportId);
    const aggAccess = new DataAccessAggregateUtils(
        mainDeal.value('industry'),
        mainDeal.value('industrySubType')
    );
    
    companies.forEach(company => {
        if (company == INDUSTRY_AVG_ID) {
            return;
        }
        const query = getQ(company);
        dataAccess[company] = query;
    });

    const getElement = (fieldName: string, rule: RuleType) => {
        const cagrPercentElement = RowValue.custom(
            (row: PanelRow, col: PanelColumn, data: any) => {
                type cagrYearly = { [key: number]: number };
                var typed: cagrYearly = data as cagrYearly;
                var keys = Object.keys(typed).sort();
                var children = [];
                for (let i = 0; i < keys.length; i++) {
                    const yearIndex = keys[i];
                    const value = typed[yearIndex];
                    // Skip entries with 0 years as they represent no data
                    if (value != null && parseInt(yearIndex) > 0) {
                        var valDisplay =
                            value == 'N/A'
                                ? 'N/A'
                                : formatPercentStr(value.toString());
                        children.push(
                            <Fragment key={yearIndex}>
                                <div>{valDisplay}</div>
                                <div key={yearIndex}>({yearIndex} years) </div>
                                <div className="w-100" />
                            </Fragment>
                        );
                    }
                }
                // If no valid entries were found, display a simple dash
                if (children.length === 0) {
                    return (
                        <Flex
                            direction="row"
                            justify="center"
                            fillCell={true}
                            wrap="wrap"
                        >
                            <div>-</div>
                        </Flex>
                    );
                }
                return (
                    <Flex
                        direction="row"
                        justify="center"
                        fillCell={true}
                        wrap="wrap"
                        style={{ gap: '5px' }}
                    >
                        {children}
                    </Flex>
                );
            }
        );

        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
        const rules = getRules();

        if (e.t == 'bool') {
            return RowValue.booleanCentered;
        } else if (e.t == '%') {
            if (rule.dataPick != null) {
                return cagrPercentElement;
            }
            return RowValue.percent;
        } else {
            return RowValue.value;
        }
    };

    const getLabel = (fieldName: string) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];

        if (e.t == '%') {
            return RowLabel.percent_label;
        } else {
            return RowLabel.label;
        }
    };

    const formatPercentWithYear = (data: any) => {
        if (!data && data !== 0) {
            data = '';
        }

        if (isString(data)) {
            return data;
        }

        interface valueFormat {
            years: number;
            value: number;
        }
        const v = data as valueFormat;
        return `${formatPercentStr(v.value.toString())} (${v.years} years)`;
    };

    const getFormatter = (fieldName: string, rule: RuleType) => {
        const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];

        if (e.t == '%') {
            if (rule.dataPick != null) {
                return noFormatRaw;
            }
            if (e.scale == 100) {
                return formatPercentNoMultiply;
            }

            return formatPercent;
        } else if (e.t == 'bool' || e.t == 'str') {
            return null;
        } else {
            return formatDollar;
        }
    };
    var scoreStore = useScoreStore();
    const fieldsGroup = scoreStore.COMPARE_FIELDS_GROUPS;
    const genColumn = (reportId: string | null, position: number) => {
        // If position is for a comparable (1 or 2) and reportId is null, show Add Deal button
        if ((position === 1 || position === 2) && reportId === null) {
            return {
                column: {
                    source: 'ADD_BUTTON',
                    display: null
                },
                header: () => (
                    <button
                        className={style.addCardButton}
                        onClick={() => {
                            // Pass the position where the user clicked
                            setComparableClickedPosition(position - 1);
                            setShowComparableSearch(true);
                        }}
                        title={`Add comparable in position ${position}`}
                    >
                        <Flex
                            direction="column"
                            alignItems="center"
                            justify="center"
                            style={{ height: '100%' }}
                        >
                            <i className="bi bi-plus-lg" style={{ fontSize: '32px', color: '#0066FF' }} />
                            <div className={style.addCardText}>Add Deal</div>
                        </Flex>
                    </button>
                )
            };
        }

        return {
            column: {
                source: reportId,
                display: null
            },
            header: (col: PanelColumn) => {
                const q = getQ(col.column.source);
                if (!q && col.column.source !== INDUSTRY_AVG_ID) {
                    return null;
                }

                const name = q?.value('company_name') || (col.column.source === INDUSTRY_AVG_ID ? 'Industry Average' : 'Unknown Company');
                let logoURL = null;
                if (col.column.source === INDUSTRY_AVG_ID) {
                    logoURL = industryIcon;
                } else {
                    logoURL = q?.value('logoURL');
                }

                return (
                    <div className={style.cardContainer}>
                        {/* Only show remove button for comparables (positions 1 and 2) */}
                        {(position === 1 || position === 2) && reportId !== null && (
                            <button 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveComparable(position - 1); // Adjust position for store array
                                }}
                                className={style.removeButton}
                                title={`Remove comparable from position ${position}`}
                            >
                                <i className="bi bi-x-lg" style={{ color: '#DD2222' }} />
                            </button>
                        )}
                        <a
                            href={`${col.column.source}`}
                            style={{ textDecoration: 'none', cursor: col.column.source === INDUSTRY_AVG_ID ? 'default' : 'pointer' }}
                            className="companyNameLabel"
                            onClick={e => {
                                e.preventDefault();
                                if (col.column.source === INDUSTRY_AVG_ID) {
                                    return;
                                }
                                navUtils.toReport(col.column.source);
                            }}
                        >
                            <div className={`${style.companyHeaderElement} ${reportId === INDUSTRY_AVG_ID ? style.industryCard : ''}`}>
                                {clientStore.showAggregateScores && q && (
                                    <DealScoreCard
                                        score={q.score()}
                                        className={`${style.dealScoreCard} ${reportId === INDUSTRY_AVG_ID ? style.industryScore : ''}`}
                                    />
                                )}
                                
                                {reportId === INDUSTRY_AVG_ID ? (
                                    <Flex
                                        direction="column"
                                        alignItems="center"
                                        justify="center"
                                        style={{ height: '100%', paddingTop: '30px', gap: '4px' }}
                                    >
                                        {logoURL && (
                                            <ImageUI
                                                src={logoURL}
                                                className={style.companyLogo}
                                            />
                                        )}
                                        {q ? (
                                            <>
                                                <div className={style.companyNameLabel}>{q.value('industry')}</div>
                                                <div className={style.industrySubType}>{q.value('industrySubType')}</div>
                                            </>
                                        ) : (
                                            <div className={style.companyNameLabel}>{name}</div>
                                        )}
                                    </Flex>
                                ) : (
                                    <Flex
                                        direction="column"
                                        alignItems="center"
                                        justify="between"
                                        style={{ height: '100%', paddingTop: '30px' }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                            <div className={style.companyNameLabel}>{name}</div>
                                            
                                            {/* Diamond icon placeholder - closer to text */}
                                            <div className={style.diamondIcon}>
                                                <i className="bi bi-diamond-fill"></i>
                                            </div>
                                        </div>

                                        {logoURL && (
                                            <ImageUI
                                                src={logoURL}
                                                className={style.companyLogo}
                                            />
                                        )}
                                    </Flex>
                                )}
                            </div>
                        </a>
                    </div>
                );
            }
        };
    };

    // Create columns array with fixed positions
    const columns = columnsData.map((reportId, index) => genColumn(reportId, index));

    const fieldsToRows = (group: ScoreFieldsGroup) => {
        const fields = group.fields;
        const rules = getRules();
        return fields.map(fieldName => {
            const e: ExtendedSchemaField = ExtendedFieldsSchema[fieldName];
            const rule = ScoreUtils.get_score_rule_by_fieldname(
                group.sectionId,
                fieldName
            );
            assert(
                e != null,
                `Field ${fieldName} not found in fieldsNameLookup`
            );
            return {
                field: {
                    source: null,
                    display: e.display
                },
                value: (_field, column) => {
                    // if (
                    //     fieldName ==
                    //     'projected_organic_cagr_3_years_minus_tam_growth'
                    // ) {
                    //     debugger;
                    // }
                    const q = getQ(column.source);
                    if (e.t == 'str') {
                        if (fieldName.indexOf('extended_') == 0) {
                            return q.get_extended_value_float(fieldName);
                        }

                        return q.value(fieldName);
                    }

                    return q.get_extended_value_float(fieldName);
                },

                label: getLabel(fieldName),
                formatter: getFormatter(fieldName, rule),
                element: getElement(fieldName, rule)
            };
        });
    };

    const groupTables = fieldsGroup.map((group, i) => {
        const sectionScoreRow = {
            field: {
                source: null,
                display: ''
            },
            value: (_field, column) => {
                const q = getQ(column.source);
                var ret = q.scoreBySection(group.sectionId as any);
                if (ret == -1) {
                    return '-';
                }

                return ret;
            },
            formatter: formatPercent,
            label: RowLabel.percent_label,
            element: (row: PanelRow, col: PanelColumn, data: any) => {
                if (!clientStore.showAggregateScores) {
                    return null;
                }

                const score = data == '-' ? -1 : data;
                return (
                    <Flex fillCell={true} alignItems="center" justify="center">
                        <DealScore score={score} />
                    </Flex>
                );
            }
        };
        const groupRows = [...fieldsToRows(group)];

        var shouldShow = companies.some(reportId => {
            if (reportId == INDUSTRY_AVG_ID) {
                return false;
            }

            if (SECTIONS_SHOW_NOT_SCORED.includes(group.sectionId)) {
                return true;
            }

            var scoreUtils = new ScoreUtils(getQ(reportId) as DataAccessUtils);
            if (scoreUtils.is_section_used_for_scoring_by_id(group.sectionId)) {
                return true;
            }

            return false;
        });

        const titlePanel = (
            <PanelTitle
                key={'title_' + group.sectionId}
                title={ScoreUtils.get_section_name(group.sectionId)}
                className={style.panelTitleText}
                containerClassName={style.panelTitle}
            >
                <SectionIcon
                    section={group.sectionId}
                    className={style.sectionIcon}
                />
            </PanelTitle>
        );
        if (!shouldShow) {
            return (
                <div key={'group_nodata_' + i}>
                    {titlePanel}
                    <Flex justify="center">
                        <div className={style.noDataAvail}>
                            No Data Available
                        </div>
                    </Flex>
                </div>
            );
        }
        return (
            <div key={'group_report_' + i}>
                {titlePanel}
                {!SECTIONS_SHOW_NOT_SCORED.includes(group.sectionId) && (
                    <div className={style.panelTableStyleRelativeContainer}>
                        <PanelTable
                            columns={columns}
                            rows={[sectionScoreRow]}
                            disable_round_edges={true}
                            className={style.panelTableStyleScoreLine}
                            noHeaderRow={true}
                        />
                    </div>
                )}
                <PanelTable
                    columns={columns}
                    rows={groupRows}
                    disable_round_edges={true}
                    className={style.panelTableStyle}
                    noHeaderRow={true}
                />
            </div>
        );
    });

    const headersTableStyle = classNames(
        style.panelTableStyle,
        style.panelTableCompanies
    );

    // Create industry row
    const industryRow = {
        field: {
            source: null,
            display: ''
        },
        value: (_field, column) => {
            const q = getQ(column.source);
            if (column.source === INDUSTRY_AVG_ID) {
                return 'Industry Average';
            }
            return q?.value('industry') || '-';
        },
        formatter: noFormat,
        element: RowValue.value
    };

    return (
        <>
            <Flex direction="column" fillCell={true}>
                <PanelTable
                    columns={columns}
                    rows={[]}
                    disable_round_edges={true}
                    className={headersTableStyle}
                />



                {/* Search modal */}
                {showComparableSearch && createPortal(
                    <Modal 
                        show 
                        onHide={() => {
                            setShowComparableSearch(false);
                            setComparableClickedPosition(null);
                        }} 
                        size="lg"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Add Comparable Deal</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <DealModalSearchContent 
                                onDealSelect={handleComparableSelect}
                                excludeDeals={[
                                    store.selectedReportId,
                                    ...store.userComparables.filter(dealId => dealId !== null && dealId !== undefined)
                                ]}
                            />
                        </Modal.Body>
                    </Modal>,
                    document.body
                )}

                <ScrollPanel>
                    {groupTables}
                    <div style={{ width: '100%', height: '100px' }}></div>
                </ScrollPanel>
            </Flex>
        </>
    );
});
